/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useMemo } from "react";

import { CosmosEnumMaterialCardBackground, GeminiCardFragment } from "@cosmos/hygraph-schema";

import {
	ButtonList,
	CosmosAsset,
	CosmosCard,
	CosmosRichText,
	CosmosText,
	hasContent,
	CosmosTextVariantEnum,
	EdsIcon,
	TagList,
	TextLinkGemini,
	TitleDescStack,
} from "@components/elements";
import type { CosmosCardProps } from "@components/elements";
import { css, cx } from "@panda/css";
import { Stack, VStack } from "@panda/jsx";

const styles = css({
	height: "100%",
	maxW: "100%",
	gap: "eds.dimension.16",
	alignItems: "stretch",
	overflow: "hidden",
});

const ContentStyles = css({
	alignItems: "start",
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	flexGrow: "1",
	minW: "0",
	height: "100%",
	maxW: "100%",
	gap: "eds.dimension.32",
});

const ThumbnailStyles = css({
	maxWidth: "253px",
	height: "auto",
	alignSelf: "stretch",
	flexShrink: "0",
	mdOnly: {
		maxWidth: "100%",
		aspectRatio: "2/1",
	},
	smOnly: {
		maxWidth: "100%",
		aspectRatio: "2/1",
	},
	xsOnly: {
		flexShrink: "1",
	},
	xsDown: {
		maxWidth: "100%",
		aspectRatio: "2/1",
	},
});

const AssetStyles = css({
	height: "100%",
	width: "253px",
	alignSelf: "stretch",
	objectFit: "cover",
});

export type GeminiCardProps = GeminiCardFragment & { animate?: boolean };

const backgroundToCardVariant = (backgorund: CosmosEnumMaterialCardBackground | null): CosmosCardProps["variant"] => {
	switch (backgorund) {
		case CosmosEnumMaterialCardBackground.Light:
			return "materialLight";
		case CosmosEnumMaterialCardBackground.Dark:
			return "materialDark";
		default:
			return "material";
	}
};

export function GeminiCard({
	eyebrow,
	heading,
	badge,
	icon,
	iconGradient,
	title,
	desc,
	links,
	buttons,
	modButtons,
	animate,
	asset,
	cardHref,
	background = CosmosEnumMaterialCardBackground.Default,
	anchorLinkId,
}: GeminiCardProps): React.JSX.Element {
	const buttonSource = modButtons?.length ? modButtons : buttons;
	const hasEyebrowContent = useMemo(() => hasContent(eyebrow), [eyebrow]);
	return (
		<VStack
			id={anchorLinkId || undefined}
			alignItems="stretch"
			gap="eds.dimension.24"
			className={cx("gemini-card-molecule")}
			minWidth="0"
		>
			<TitleDescStack title={{ ...heading, variant: "headingXL", as: "h4" }} className="gemini-card-heading" />
			<CosmosCard
				className={cx("gemini-card", styles)}
				variant={backgroundToCardVariant(background)}
				direction="geminiDir"
				padding={asset ? 16 : 24}
			>
				<CosmosCard.Content className={cx("gemini-card-content", ContentStyles)}>
					<Stack
						justify="start"
						rowGap="eds.dimension.24"
						columnGap="eds.dimension.16"
						height="100%"
						maxW="100%"
						minW="0"
						className={cx("gemini-card-content-top")}
					>
						<VStack className="gemini-link-list" gap="eds.dimension.16" alignItems="flex-start">
							{badge ? <TagList tags={[badge]}></TagList> : null}
							<EdsIcon icon={icon} size="xl" gradient={iconGradient} />
							<CosmosCard.Link
								href={cardHref || ""}
								className={css({
									width: "100%",
									display: "flex",
									flexDirection: "column",
									textAlign: "start",
									gap: hasEyebrowContent ? "eds.dimension.24" : "eds.dimension.16",
								})}
							>
								{hasEyebrowContent ? (
									<CosmosText variant="eyebrowMD" text={eyebrow} color="eds.text.secondary" />
								) : null}
								{title?.html ? (
									<h3>
										<CosmosRichText
											variant={CosmosTextVariantEnum.headingMD}
											color="eds.text.contrast"
											html={title.html}
											className={css({ overflowWrap: "anywhere" })}
										/>
									</h3>
								) : null}
								{desc ? (
									<CosmosRichText
										variant={CosmosTextVariantEnum.paragraphSM}
										color={hasEyebrowContent ? "eds.text.primary" : "eds.text.secondary"}
										html={desc}
										className={css({ overflowWrap: "anywhere" })}
									/>
								) : null}
							</CosmosCard.Link>
						</VStack>
						{links?.length ? (
							<VStack className="gemini-link-list" gap="eds.dimension.12" alignItems="flex-start">
								{links.map((link, i) => (
									<TextLinkGemini key={`gemini-link-${i}`} {...link} />
								))}
							</VStack>
						) : null}
					</Stack>
					<ButtonList animate={animate} buttons={buttonSource} size="lg" />
				</CosmosCard.Content>
				{asset ? (
					<CosmosCard.Thumbnail className={ThumbnailStyles} aspectRatio="unset">
						<CosmosAsset asset={asset} className={AssetStyles} />
					</CosmosCard.Thumbnail>
				) : null}
			</CosmosCard>
		</VStack>
	);
}
